// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { CameraSource } from "@capacitor/camera";
import { type UseModalState } from "@clipboard-health/ui-components";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useBrowserOnFileChange } from "@src/appV2/lib/Media/useBrowserOnFileChange";
import { useMobileMultiFileSelect } from "@src/appV2/lib/Media/useMobileMultiFileSelect";
import { useMobileOnFileSelect } from "@src/appV2/lib/Media/useMobileOnFileSelect";
import { usePickImages } from "@src/appV2/lib/Media/usePickImages";
import { useTakePhoto } from "@src/appV2/lib/Media/useTakePhoto";
import { FileUploaderBottomSheet } from "@src/appV2/redesign/components/FileUploaderBottomSheet";
import { type UploadedFile } from "@src/appV2/redesign/Documents/useFileUploaderContext";
import { useRef } from "react";
import { v4 as uuidv4 } from "uuid";

export const IGNORED_FILE_UPLOAD_ERROR_MESSAGES = {
  IMAGE_NOT_SELECTED: "No image picked",
  CAMERA_PHOTO_NOT_TAKEN: "User cancelled photos app",
  PICK_FILES_CANCELED: "pickFiles canceled.",
};

interface FileSelectionBottomSheetProps {
  modalState: UseModalState;
  contentTypes: string[];
  onFileAdded: (file: UploadedFile) => void;
  onFilesAdded: (files: UploadedFile[]) => void;
  onCameraPermissionDenied: () => void;
}

function toUploadedFile(file: UploadFileData): UploadedFile {
  return {
    id: uuidv4(),
    dataUrlBase64: file.dataUrlBase64,
    extension: file.extension,
    contentType: file.contentType,
  };
}

export function FileSelectionBottomSheet(props: FileSelectionBottomSheetProps) {
  const { modalState, contentTypes, onFileAdded, onFilesAdded, onCameraPermissionDenied } = props;

  const { showErrorToast } = useToast();
  const inputFileRef = useRef<HTMLInputElement>(null);

  const configDocumentUploadMultiSelect = useCbhFlag(
    CbhFeatureFlag.CONFIG_DOCUMENT_UPLOAD_MULTI_SELECT,
    {
      defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.CONFIG_DOCUMENT_UPLOAD_MULTI_SELECT],
    }
  );

  const onSuccess = (photo: UploadFileData) => {
    modalState.closeModal();
    onFileAdded(toUploadedFile(photo));
  };

  const onMultiSuccess = (files: UploadFileData[]) => {
    modalState.closeModal();
    onFilesAdded(files.map(toUploadedFile));
  };

  const onError = (error: Error | CameraPermissionDeniedError) => {
    if (error instanceof CameraPermissionDeniedError) {
      onCameraPermissionDenied();
      return;
    }

    if (Object.values(IGNORED_FILE_UPLOAD_ERROR_MESSAGES).includes(error?.message)) {
      return;
    }

    showErrorToast(
      error.message ?? "We're having trouble processing your photo. Please try again."
    );
  };

  const handleTakePhoto = useTakePhoto({
    source: CameraSource.Camera,
    onSuccess,
    onError,
  });

  const handleSelectPhotoFromCameraRoll = useTakePhoto({
    source: CameraSource.Photos,
    onSuccess,
    onError,
  });

  const handleSelectMultiplePhotosFromCameraRoll = usePickImages({
    onSuccess: onMultiSuccess,
    onError,
  });

  const handleSelectMultipleFiles = useMobileMultiFileSelect({
    onSuccess: onMultiSuccess,
    onError,
    allowMultiple: true,
    contentTypes,
  });

  const handleSelectFile = useMobileOnFileSelect({
    onSuccess,
    onError,
    contentTypes,
  });

  const browserSelectFile = async () => {
    inputFileRef?.current?.click();
  };

  const browserOnFileChange = useBrowserOnFileChange({
    onSuccess,
    onError,
  });

  const handleCapacitorFileSelect = configDocumentUploadMultiSelect.multiFile
    ? handleSelectMultipleFiles
    : handleSelectFile;

  const handleBrowserFileSelect = configDocumentUploadMultiSelect.multiFile
    ? handleSelectMultipleFiles
    : browserSelectFile;

  return (
    <>
      <input
        ref={inputFileRef}
        style={{ display: "none" }}
        type="file"
        accept={contentTypes.join(",")}
        onChange={browserOnFileChange}
      />
      <FileUploaderBottomSheet
        modalState={modalState}
        onTakePhoto={isCapacitorPlatform() ? handleTakePhoto : handleSelectPhotoFromCameraRoll}
        onSelectPhotoFromCameraRoll={
          configDocumentUploadMultiSelect.multiPhoto
            ? handleSelectMultiplePhotosFromCameraRoll
            : handleSelectPhotoFromCameraRoll
        }
        onSelectFile={isCapacitorPlatform() ? handleCapacitorFileSelect : handleBrowserFileSelect}
      />
    </>
  );
}
